<template>
  <div>
    <breadcrumbs :home="item.name + ': Adjust inventory'"/>

      <v-card flat>
        <v-form v-model="valid" ref="form" :disabled="!edit">
          <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

            <v-row dense>
              <v-col cols="6" sm="4" lg="3">
                <date-picker v-model="transaction.date" @change="refresh"/>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="6" sm="3" lg="2" v-if="item.lotTracking" >
                <v-combobox v-model="transaction.lot" outlined dense label="Lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]" @change="setAdjustedValues()"/>
              </v-col>
              <v-col cols="12" sm="6" lg="4" v-if="sites.length>1">
                <v-autocomplete outlined dense label="Site" v-model="transaction.siteId" :items="sites" item-value="id" item-text="name" @change="setAdjustedValues()"/>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col>
                <v-radio-group v-model="transaction.specifyDifference" dense class="mt-0" row @change="setAdjustedValues()">
                  <v-radio label="Adjust by difference" :value="true"/>
                  <v-radio label="Adjust to specified quantity/value" :value="false"/>
                </v-radio-group>
              </v-col>
            </v-row>
            <v-row dense  v-if="transaction.specifyDifference">
              <v-col cols="3" sm="2" lg="1">
                <v-btn-toggle mandatory dense v-model="negative" @change="calculateValue = (negative===1)">
                  <v-btn icon :disabled="!edit" :value="false"><v-icon small>mdi-plus</v-icon></v-btn>
                  <v-btn icon :disabled="!edit" :value="true"><v-icon small>mdi-minus</v-icon></v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input label="Adjust by" v-model="quantity" :precision="item.precision" prefix="" :suffix="item.trackUnits"
                                      :hint="negative&&!transactionId?currentQuantity:''"/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input label="Total value" v-model="value" :disabled="calculateValue"/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <v-checkbox label="Calculate value" v-model="calculateValue" dense class="mt-1" :disabled="!negative"/>
              </v-col>
            </v-row>
            <template v-else>
              <v-row dense>
                <v-col cols="6" sm="3" lg="2">
                  <decimal-number-input label="Adjusted quantity" v-model="transaction.quantity" :precision="item.precision" prefix="" :suffix="item.trackUnits" :no-minimum="item.allowNegativeInventory"/>
                </v-col>
                <v-col cols="6" sm="3" lg="2">
                  <decimal-number-input label="Total adjusted value" v-model="transaction.value" :no-minimum="item.allowNegativeInventory"/>
                </v-col>
              </v-row>
              <v-row dense>
                <v-col class="text-caption mb-6">Adjustment will be saved as "Adjust by difference" with calculated difference</v-col>
              </v-row>
            </template>

            <v-row dense v-if="values && $store.getters.company.connectedToAccounting">
              <v-col cols="12" sm="6" lg="4">
                <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                                :accounts="accounts"
                                hint="An account to balance the inventory asset account. Usually an inventory shrinkage expense or cost of goods account."/>
              </v-col>
            </v-row>
            <v-row dense >
              <v-col cols="12">
                <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
        <v-divider/>
        <v-card-actions class="px-6">
          <v-switch v-if="transactionId && $store.getters.company.permissions.includes('inventory.write')"
                    v-model="edit" label="Edit" hide-details class="mt-0" :disabled="dirty"/>
          <v-spacer/>
          <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
          <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'

export default {
  name: 'InventoryItemsAdjustInventory',
  mixins: [confirmLeave],
	props: {
    itemId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      item: {name:'',sites:[]},
      valid:true,
      saving:false,
      accounts: [],
      calculateValue: false,
      value: 0,
      quantity: 0,
      negative: false,
      transaction: {itemId:this.itemId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), specifyDifference:true, quantity: 0, value:0, siteId:'', lot:'', comments: '', expenseAccountRef:''},
      dirty: false
		}
  },
  computed: {
    sites() {
      return this.$store.getters.sites(!this.transactionId);
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    },
    currentLots() {
      return this.item.sites.filter(site => site.siteId === this.transaction.siteId).map(site => site.lot);
    },
    currentQuantity() {
      const site = this.item.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lot);
      return site ? this.$number(site.quantity, this.item.precision) : '';
    }
  },
  watch: {
    calculateValue() {
      this.dirty = true;
      if (this.calculateValue)
        this.transaction.value = null;
    },
    value() {
      this.dirty = true;
    },
    negative() {
      this.dirty = true;
    },
    quantity() {
      this.dirty = true;
    },
    transaction: {
      handler() {
        this.dirty = true;
      },
      immediate: true,
      deep: true
    }
  },
	async created() {
    this.transaction.siteId = this.sites[0].id;
    const results = await this.$apiCall(`query InventoryItemsAdjustInventory($currency:String!,$id:ID!,$edit:Boolean!) {
                                              accounts(currency:$currency) {
                                                ref,parentRef,name,type,typeName
                                              }
                                              itemAdjustInventory(id:$id) @include(if:$edit) {
                                                itemId,date,specifyDifference,siteId,lot,quantity,value,comments,expenseAccountRef
                                              }
                                              companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                            }`,
                                        {currency:this.$store.getters.company.settings.homeCurrency,
                                         id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.accounts = results.data.accounts;
    if (this.transactionId) {
      this.transaction = results.data.itemAdjustInventory;
      this.transaction.id = this.transactionId;
      this.calculateValue = !this.transaction.value && this.transaction.quantity < 0;
      this.negative = this.transaction.quantity < 0;
      this.value = Math.abs(this.transaction.value || 0);
      this.quantity = Math.abs(this.transaction.quantity);
    }
    await this.refresh();
    if (!this.transactionId) {
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
      this.edit = true;
      this.$nextTick(() => {
        this.$refs.form.validate();
      })
    }

    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    setAdjustedValues() {
      if (!this.specifyDifference) {
        const from = this.item.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lot) ?? {quantity:0,value:0};
        this.transaction.quantity = from.quantity;
        this.transaction.value = from.value;
      }
    },
    async refresh() {
      const results = await this.$apiCall(`query InventoryItemsAdjustInventory($id:ID!, $date:DateTime!) {
                                                item(id:$id) {
                                                  id,name,active,allowNegativeInventory,trackUnits,precision,lotTracking,
                                                  sites(asof:$date) {siteId,lot,quantity,value}
                                                }
                                              }`,
                                          {id:this.transaction ? this.transaction.itemId : this.itemId,
                                           date:this.transaction.date});
      if (!results.error) {
        this.item = results.data.item;
        this.$nextTick(() => {
          this.$refs.form.validate();
        })
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      if (this.valid) {
        const transaction = {...this.transaction};
        if (transaction.specifyDifference)
          Object.assign(transaction, {value:this.calculateValue ? null : (this.negative?-this.value:this.value),
                                      quantity:this.negative?-this.quantity:this.quantity});
        this.saving = true;
        const results = await this.$apiCall(`mutation InventoryItemsAdjustInventory($input: ItemAdjustInventoryInput!) {
                                                itemAdjustInventoryCreate(input:$input) {id}
                                              }`,
                                            {input:transaction});
        this.saving = false;
        if (!results.error) {
          this.dirty = false;
          this.$router.back();
          this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} adjust inventory transaction for item ${this.item.name}`});
        }
      }
		}
	}
}
</script>